import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import logo from "../../images/logo.png";
import void_check from "../../images/void_check_can.jpg";

const DirectDeposit = () => {
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const loggedInId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";
  const userId = isAdmin && paramId ? paramId : loggedInId;

  const [form, setForm] = useState({});
  const [file, setFile] = useState(null);
  const [uploadedFile, setUploadedFile] = useState({
    filename: null,
    original: null,
  });
  const [isSigned, setIsSigned] = useState(false);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];

    if (allowedTypes.includes(selected.type)) {
      setFile(selected);
    } else {
      alert("Only PDF, JPG, or PNG files are allowed!");
      setFile(null);
    }
  };

  const fetchDepositInfo = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/directdeposit/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setForm(res.data);
      setUploadedFile({
        filename: res.data.dd_directdeposit,
        original: res.data.dd_directdeposit_original,
      });
    } catch (err) {
      console.log("No direct deposit info found.");
    }
  }, [API_BASE, userId]);

  const fetchTaskStatus = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API_BASE}/api/usertasks/status/${userId}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      const TASK_ID = 6; // verify this matches nwt_tasks.t_id for LEEP
      const idKey = String(TASK_ID);
      const nameKey = "direct_deposit";

      const status =
        res.data.status?.[idKey] ??
        res.data.status?.[nameKey] ??
        "not_completed";

      setIsSigned(status === "review" || status === "completed");
    } catch (err) {
      console.error("Error checking signature status:", err);
    }
  }, [API_BASE, userId]);

  useEffect(() => {
    fetchDepositInfo();
    fetchTaskStatus();
  }, [fetchDepositInfo, fetchTaskStatus]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData();
    data.append("file", file);
    data.append("userId", userId);
    Object.entries(form).forEach(([key, value]) => {
      data.append(key, value || "");
    });

    try {
      const res = await axios.post(
        `${API_BASE}/api/directdeposit/upload?userId=${userId}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      alert("Direct deposit info saved successfully!");
      setUploadedFile({
        filename: res.data.filename,
        original: res.data.originalName,
      });
    } catch (err) {
      alert("Failed to save data");
      console.error(err);
    }
  };

  return (
    <>
      {/* 🔝 Header Section with Status + Cancel Button */}
      <div className="d-flex justify-content-between align-items-center pt-3">
        <div className="d-flex align-items-center">
          <span className="me-2 text-danger"></span>
          <span style={{ fontSize: "18px", fontWeight: "500" }}>
            Direct Deposit
          </span>
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => navigate(-1)}
        >
          Return
        </button>
      </div>

      <div className="form-wrapper my-4">
        <div className="row align-items-center mb-4">
          <div className="col-3 text-start">
            <img
              src={logo}
              alt="Company Logo"
              style={{ width: "150px", height: "auto" }}
            />
          </div>
          <div className="col-6 text-center">
            <h2 className="m-0">Direct Deposit Authorization</h2>
          </div>
          <div className="col-3 text-end">
            <strong>New-Way Trucking Ltd</strong>
            <br />
            7725 48 St. SE, Calgary
            <br />
            Alberta, Canada T2C 2V3
          </div>
        </div>
        <form onSubmit={handleSubmit}>
          {/* Primary Account */}
          <h5 className="my-4 ">Primary Account</h5>
          <div className="mb-3">
            <label className="form-label">Institution Name</label>
            <input
              className="form-control"
              name="dd_institution_name"
              value={form.dd_institution_name || ""}
              onChange={handleChange}
              disabled={!isAdmin && isSigned}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Institution Address 1</label>
            <input
              className="form-control"
              name="dd_institution_address1"
              value={form.dd_institution_address1 || ""}
              onChange={handleChange}
              disabled={!isAdmin && isSigned}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Institution Address 2</label>
            <input
              className="form-control"
              name="dd_institution_address2"
              value={form.dd_institution_address2 || ""}
              onChange={handleChange}
              disabled={!isAdmin && isSigned}
            />
          </div>

          <label className="form-label">Complete Account No.</label>
          <div className="row mb-4">
            <div className="col-md-3">
              <input
                className="form-control"
                name="dd_branch_code"
                placeholder="Branch Code"
                value={form.dd_branch_code || ""}
                onChange={handleChange}
                disabled={!isAdmin && isSigned}
              />
            </div>
            <div className="col-md-3">
              <input
                className="form-control"
                name="dd_institution_code"
                placeholder="Institution Code"
                value={form.dd_institution_code || ""}
                onChange={handleChange}
                disabled={!isAdmin && isSigned}
              />
            </div>
            <div className="col-md-3">
              <input
                className="form-control"
                name="dd_account_number"
                placeholder="Account Number"
                value={form.dd_account_number || ""}
                onChange={handleChange}
                disabled={!isAdmin && isSigned}
              />
            </div>
            <div className="col-md-3">
              <select
                className="form-select"
                name="dd_account_type"
                value={form.dd_account_type || ""}
                onChange={handleChange}
                disabled={!isAdmin && isSigned}
              >
                <option value="">Account Type</option>
                <option value="Chequing Account">Chequing Account</option>
                <option value="Savings Account">Savings Account</option>
              </select>
            </div>
          </div>

          <hr className="my-4" />

          {/* Secondary Account */}
          <h5 className="mb-4">Secondary Account</h5>
          <div className="mb-3">
            <label className="form-label">Institution Name</label>
            <input
              className="form-control"
              name="dd_secondary_name"
              value={form.dd_secondary_name || ""}
              onChange={handleChange}
              disabled={!isAdmin && isSigned}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Institution Address 1</label>
            <input
              className="form-control"
              name="dd_secondary_address1"
              value={form.dd_secondary_address1 || ""}
              onChange={handleChange}
              disabled={!isAdmin && isSigned}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Institution Address 2</label>
            <input
              className="form-control"
              name="dd_secondary_address2"
              value={form.dd_secondary_address2 || ""}
              onChange={handleChange}
              disabled={!isAdmin && isSigned}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Amount</label>
            <input
              className="form-control"
              name="dd_secondary_amount"
              value={form.dd_secondary_amount || ""}
              onChange={handleChange}
              disabled={!isAdmin && isSigned}
            />
          </div>

          <label className="form-label">Complete Account No.</label>
          <div className="row mb-4">
            <div className="col-md-3">
              <input
                className="form-control"
                name="dd_secondary_branch_code"
                placeholder="Branch Code"
                value={form.dd_secondary_branch_code || ""}
                onChange={handleChange}
                disabled={!isAdmin && isSigned}
              />
            </div>
            <div className="col-md-3">
              <input
                className="form-control"
                name="dd_secondary_institution_code"
                placeholder="Institution Code"
                value={form.dd_secondary_institution_code || ""}
                onChange={handleChange}
                disabled={!isAdmin && isSigned}
              />
            </div>
            <div className="col-md-3">
              <input
                className="form-control"
                name="dd_secondary_account_number"
                placeholder="Account Number"
                value={form.dd_secondary_account_number || ""}
                onChange={handleChange}
                disabled={!isAdmin && isSigned}
              />
            </div>
            <div className="col-md-3">
              <select
                className="form-select"
                name="dd_secondary_account_type"
                value={form.dd_secondary_account_type || ""}
                onChange={handleChange}
                disabled={!isAdmin && isSigned}
              >
                <option value="">Account Type</option>
                <option value="Chequing Account">Chequing Account</option>
                <option value="Savings Account">Savings Account</option>
              </select>
            </div>
          </div>

          <hr className="my-4" />

          <div className="form-text mb-3">
            Voided cheque must be attached or account information attached from
            your banking institution.
          </div>

          {/* Associate Info */}
          <div className="mb-3">
            <label className="form-label">Associate's Name</label>
            <input
              className="form-control"
              name="dd_associate_name"
              value={form.dd_associate_name || ""}
              onChange={handleChange}
              disabled={!isAdmin && isSigned}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Associate Address</label>
            <input
              className="form-control"
              name="dd_associate_address"
              value={form.dd_associate_address || ""}
              onChange={handleChange}
              disabled={!isAdmin && isSigned}
            />
          </div>
          <div className="mb-4">
            <label className="form-label">Associate Phone</label>
            <input
              className="form-control"
              name="dd_associate_phone"
              value={form.dd_associate_phone || ""}
              onChange={handleChange}
              disabled={!isAdmin && isSigned}
            />
          </div>

          <div className="mt-5">
            <h5>Sample of voided cheque & Account Number</h5>
            <p className="text-muted">
              The branch code, institution & account number must be visible on
              the cheque.
            </p>
            <div className="text-start">
              <img
                src={void_check}
                alt="Sample of voided cheque"
                className="img-fluid border rounded shadow-sm"
                style={{ maxWidth: "600px" }}
              />
            </div>
          </div>

          <div className="mt-5 mb-3">
            <label>Select File (PDF / JPG / PNG) *</label>
            <input
              type="file"
              className="form-control"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileChange}
              disabled={!isAdmin && isSigned}
            />
          </div>
          <div className="d-flex justify-content-end">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!isAdmin && isSigned}
            >
              Update
            </button>
          </div>
        </form>

        {/* 🔗 Uploaded File */}
        {uploadedFile.filename && (
          <div className="mt-4 p-3 bg-light rounded border">
            <div className="text-muted mb-2">Uploaded Document</div>
            <a
              href={`${API_BASE}/uploads/directdeposit/${uploadedFile.filename}`}
              target="_blank"
              rel="noopener noreferrer"
              download={uploadedFile.original}
            >
              {uploadedFile.original}
            </a>
          </div>
        )}

        <hr className="my-5" />

        {/* 🖊 Signature */}
        {userId && (
          <>
            <SignatureManager
              taskName="direct_deposit"
              taskId={6}
              userId={userId}
              isAdmin={isAdmin}
            />
            <ReviewManager
              taskName="direct_deposit"
              taskId={6}
              userId={userId}
              isAdmin={isAdmin}
            />
          </>
        )}
      </div>
    </>
  );
};

export default DirectDeposit;
